import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = '/api';
const HEARTBEAT_INTERVAL = 25000; // 25 seconds
const IDLE_TIMEOUT = 300000; // 5 minutes
const BATCH_INTERVAL = 10000; // 10 seconds

/**
 * EngagementTracker — Invisible tracking hook
 * Auto-fires on route changes, heartbeats when active, batches events.
 * No UI impact. Async non-blocking.
 */
export function useEngagementTracker(user) {
  const location = useLocation();
  const sessionIdRef = useRef(null);
  const eventQueueRef = useRef([]);
  const lastPageRef = useRef(null);
  const lastPageTimeRef = useRef(null);
  const isActiveRef = useRef(true);
  const heartbeatRef = useRef(null);
  const batchRef = useRef(null);
  const idleTimerRef = useRef(null);

  const getPageName = useCallback((pathname) => {
    const map = {
      '/dashboard': 'dashboard', '/daily': 'daily', '/mindflo': 'mindflo',
      '/decode': 'decode', '/protocol': 'protocol', '/tracking': 'tracking',
      '/wearables': 'wearables', '/my-intake': 'my-intake', '/my-labs': 'my-labs',
      '/my-reports': 'my-reports', '/eat': 'kitchen', '/knowledge': 'knowledge',
      '/support': 'support',
    };
    return map[pathname] || pathname.replace(/^\//, '') || 'other';
  }, []);

  const queueEvent = useCallback((event_type, page_name, metadata) => {
    eventQueueRef.current.push({
      event_type, page_name,
      event_time: new Date().toISOString(),
      metadata: metadata || null,
    });
  }, []);

  const flushEvents = useCallback(async () => {
    if (!sessionIdRef.current || eventQueueRef.current.length === 0) return;
    const events = [...eventQueueRef.current];
    eventQueueRef.current = [];
    try {
      await axios.post(`${API}/engagement/events`, {
        session_id: sessionIdRef.current,
        events,
      });
    } catch {
      // Re-queue on failure silently
      eventQueueRef.current = [...events, ...eventQueueRef.current].slice(0, 100);
    }
  }, []);

  const sendHeartbeat = useCallback(async () => {
    if (!sessionIdRef.current || !isActiveRef.current) return;
    try {
      await axios.post(`${API}/engagement/session/heartbeat`, {
        session_id: sessionIdRef.current,
        current_page: lastPageRef.current,
      });
    } catch { /* silent */ }
  }, []);

  const resetIdleTimer = useCallback(() => {
    isActiveRef.current = true;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => { isActiveRef.current = false; }, IDLE_TIMEOUT);
  }, []);

  // Start session on mount
  useEffect(() => {
    if (!user?.id || user.role !== 'subscriber') return;

    const startSession = async () => {
      try {
        const ua = navigator.userAgent;
        const isMobile = /Mobile|Android|iPhone/.test(ua);
        const res = await axios.post(`${API}/engagement/session/start`, {
          device_type: isMobile ? 'mobile' : 'desktop',
          browser: navigator.userAgent.slice(0, 100),
          entry_page: getPageName(window.location.pathname),
        });
        sessionIdRef.current = res.data?.session_id;
      } catch { /* silent */ }
    };
    startSession();

    // Heartbeat interval
    heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
    // Batch flush interval
    batchRef.current = setInterval(flushEvents, BATCH_INTERVAL);

    // Activity listeners
    const onActivity = () => resetIdleTimer();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        isActiveRef.current = false;
        flushEvents();
      } else {
        resetIdleTimer();
      }
    };
    const onBeforeUnload = () => {
      flushEvents();
      if (sessionIdRef.current) {
        navigator.sendBeacon(`${API}/engagement/session/end`, JSON.stringify({
          session_id: sessionIdRef.current,
          exit_page: lastPageRef.current,
        }));
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });
    window.addEventListener('beforeunload', onBeforeUnload);
    resetIdleTimer();

    return () => {
      clearInterval(heartbeatRef.current);
      clearInterval(batchRef.current);
      clearTimeout(idleTimerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('touchstart', onActivity);
      window.removeEventListener('beforeunload', onBeforeUnload);
      flushEvents();
    };
  }, [user?.id, user?.role, getPageName, sendHeartbeat, flushEvents, resetIdleTimer]);

  // Track page changes
  useEffect(() => {
    if (!user?.id || user.role !== 'subscriber') return;
    const pageName = getPageName(location.pathname);

    // Record duration of previous page
    if (lastPageRef.current && lastPageTimeRef.current) {
      const duration = Math.round((Date.now() - lastPageTimeRef.current) / 1000);
      queueEvent('leave_page', lastPageRef.current, { duration_seconds: duration });
    }

    // Record new page open
    queueEvent('open_page', pageName);
    lastPageRef.current = pageName;
    lastPageTimeRef.current = Date.now();
  }, [location.pathname, user?.id, user?.role, getPageName, queueEvent]);

  // Public method to track custom events
  const trackEvent = useCallback((eventType, metadata) => {
    if (!user?.id || user.role !== 'subscriber') return;
    queueEvent(eventType, lastPageRef.current || 'unknown', metadata);
  }, [user?.id, user?.role, queueEvent]);

  return { trackEvent };
}
